import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { AimChatPanel } from '../components/aim/AimChatPanel.js';
import { AimConversationList } from '../components/aim/AimConversationList.js';
import { adminAimApi, type AimConversationSummary } from '../lib/api.js';

// Lane D2 (D5) — the /ai/manager page: conversation sidebar + chat panel.
// Server surface: /api/v2/admin/ai (ai-manager:use:all via the deny-by-
// default guard). Mobile-first: the sidebar collapses to a toggle row
// under 900px; the chat panel is the primary surface.

type AiManagerProps = {
  config: ClientConfig;
};

export function AiManager({ config }: AiManagerProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const [conversations, setConversations] = useState<AimConversationSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showListMobile, setShowListMobile] = useState(false);

  // Sawyer 17 ai-manager-help (F5) — the top-bar Help lands here with
  // ?help=1: a NEW conversation in help mode. Every Help tap is a new
  // history entry (new location.key), so a second tap from inside the page
  // starts fresh too. The reset happens during render (React's "adjust
  // state when a prop changes" pattern) so the panel never mounts with the
  // previously open conversation and starts loading it.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const helpMode = searchParams.get('help') === '1';
  const [helpEntryKey, setHelpEntryKey] = useState<string | null>(null);
  if (helpMode && helpEntryKey !== location.key) {
    setHelpEntryKey(location.key);
    setActiveId(null);
  }
  const leaveHelpMode = useCallback(() => {
    if (helpMode) setSearchParams({}, { replace: true });
  }, [helpMode, setSearchParams]);

  const refreshList = useCallback(async () => {
    const res = await adminAimApi.listConversations(apiUrl);
    if (res.data) {
      setConversations(res.data.conversations);
      setListError(false);
    } else {
      // #429-adjacent — a failed list load must NOT render as the
      // "No conversations yet." empty state (design constitution: error
      // and empty are distinct mandatory states).
      setListError(true);
    }
    setListLoading(false);
  }, [apiUrl]);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  const handleArchive = useCallback(
    async (id: string) => {
      await adminAimApi.archiveConversation(apiUrl, id);
      if (id === activeId) setActiveId(null);
      void refreshList();
    },
    [apiUrl, activeId, refreshList],
  );

  return (
    <div className="ferro-aim" data-testid="aim-page">
      <header className="ferro-aim__header">
        <div>
          <h1 className="ferro-section__title">AI Manager</h1>
          <p className="ferro-aim__subtitle">
            Your operations assistant — schedules, customers, compliance, and how to use the app.
          </p>
        </div>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-aim__history-toggle"
          aria-expanded={showListMobile}
          onClick={() => setShowListMobile((v) => !v)}
        >
          {showListMobile ? 'Hide history' : 'History'}
        </button>
      </header>

      <div className={`ferro-aim__layout${showListMobile ? ' ferro-aim__layout--list-open' : ''}`}>
        <AimConversationList
          conversations={conversations}
          activeId={activeId}
          loading={listLoading}
          error={listError}
          onRetry={() => {
            setListLoading(true);
            void refreshList();
          }}
          onSelect={(id) => {
            setActiveId(id);
            setShowListMobile(false);
            leaveHelpMode(); // Sawyer 17 ai-manager-help (F5)
          }}
          onNew={() => {
            setActiveId(null);
            setShowListMobile(false);
            leaveHelpMode(); // Sawyer 17 ai-manager-help (F5)
          }}
          onArchive={(id) => void handleArchive(id)}
        />
        <AimChatPanel
          // Sawyer 17 ai-manager-help (F5, C7) — each Help tap remounts the
          // panel, so a failed first send or a typed draft never survives it.
          key={helpMode ? `help-${location.key}` : 'ops'}
          mode={helpMode ? 'help' : 'ops'}
          apiUrl={apiUrl}
          conversationId={activeId}
          onConversationCreated={(id) => setActiveId(id)}
          onExchangeComplete={() => void refreshList()}
        />
      </div>
    </div>
  );
}
